import { pricingRuleController } from "./PricingRules/pricingRuleController";
import { message, Modal } from "antd";
import { v4 as uuidv4 } from "uuid";
import NotFoundImg from "../../../assets/images/NotFoundImg.svg";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";
import PoleDisplay from "../../../lib/printer/poleDisplay";
import CWPoleDisplay from "../../../lib/printer/CWPoleDisplay";
import db from "../../../database";
import dayjs from "dayjs";
import sumBy from "lodash/sumBy";
import { stock } from "./stock";
import { discardGiftCard, discardCouponService } from "../../../services";
import { numberToWords } from "../../../utils/numberToWords";
import { clock, timeStamp, timeValidator, currentDay, dateValidator } from "../../../utils/clock";
import messages from "../../../errors/messages.en-IN.json";
import { roundTo, truncateToPrecision, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../utils/tillHelpers";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useCartStore from "../../../stores/useCartStore";
import useTransactionStore from "../../../stores/useTransactionStore";
import useRestaurantStore from "../../../stores/useRestaurantStore";
import { getPricingRules, findPricingRuleById } from "../../../utils/pricingRulesCache";
import { getIsInterState } from "../../../utils/taxUtils";
import { groupItemsByTaxRate } from "./utils/taxCalculations";
import { trace } from "../../../utils/debugTrace";
import { computeIsExchangeTarget } from "../../../utils/exchangeCartRouting";

// True when `record` is one of the cart store's own line objects (top-level line or
// a nested child/childItem), compared by reference.
const isLiveCartLine = (record) => {
  if (!record || typeof record !== "object") return false;
  const { cart, exchangeCart } = useCartStore.getState();
  const containsRecord = (items) =>
    Array.isArray(items) && items.some((item) => item === record || containsRecord(item?.children) || containsRecord(item?.childItems));
  return containsRecord(cart?.items) || containsRecord(exchangeCart?.items);
};

// A quantity edit resets the line's discount before the bill discounts are replayed below. The
// amount each Total Bill Discount recorded on the line (allDiscounts[].appliedAmount) is then no
// longer part of that discount, so it is dropped with it; CheckoutTotalManualDiscount would
// otherwise subtract it again on the replay (discount removed or negative after a qty change).
const dropAppliedDiscountAmounts = (line) => {
  if (!Array.isArray(line?.allDiscounts)) return;
  line.allDiscounts = line.allDiscounts.map((entry) => {
    const kept = { ...entry };
    delete kept.appliedAmount;
    return kept;
  });
};

export const addProductToCart = async (
  addToCart,
  qty,
  totalQtyFlag,
  cart,
  setCart,
  setSelectedProductInCart,
  deleteCart,
  processTotalManualDiscount,
  setLoader,
  salesRepresentDefaultLine,
  tillData,
  cartRef,
  productsCopy,
  salesRepresent,
  orderType,
  processBillDiscounts,
  modifiedPrice,
  setSelectedRowKeys,
  setOrderTimeDetails,
  setDisplayAddOnSelection,
  displayAddOnSelection,
  handleAddOnModal,
  manualDiscountTypes,
  CheckoutFlatDiscount,
  CheckoutPercentageDiscount,
  CheckoutTotalManualDiscount,
  setProductStock,
  selectedProductInCart,
  setTableCards,
  dispatchItemSelection,
  searchInputRef,
  deleteProductInCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  TotalBillDiscount,
  tillLayout,
  quantityChange,
  setManualDiscountInput,
  setSelectedManualDiscountTypeValue,
  removeAllDiscounts,
  setSelectedPaymentMethod,
  selectedReturnType,
  customMixHandler,
  setProductBOMStock,
  setProductBOMStockData
) => {
  // BUG-2467: a delete/qty edit passes the cart table's row, which is the store's own
  // line object. This function writes weight (and other fields) onto addToCart before
  // the final setCart, so a write that setCart then rejects (advance paid-amount floor)
  // still left the store line at weight 0: the line vanished from the screen while the
  // totals stayed, and the order completed with a qty-0 line carrying its old amount.
  // Work on a copy whenever the record is a live cart line.
  if (isLiveCartLine(addToCart)) {
    addToCart = JSON.parse(JSON.stringify(addToCart));
  }
  const returnThisReceipt = useTransactionStore.getState().returnThisReceipt;
  const isExchange = computeIsExchangeTarget({
    selectedReturnType: useTransactionStore.getState().selectedReturnType,
    returnThisReceipt,
    cart: useCartStore.getState().cart || cart,
  });
  const weight = parseFloat(qty);
  // BUG-2041 (Stage-1 gap, TIC-6916/TSK-3355): every OTHER place this file mutates a
  // line's weight already guards against an unparseable qty (edit-merge ~L521:
  // `!(x > 0)`, cancel-restore, parked-restore) -- addProductToCart's own entry point
  // never did, so a NaN `weight` here silently flowed into every branch below (new-line
  // add, grouped/Custom-Mix children, childItems) and their mrp/tax/nettotal/linegross
  // arithmetic, without ever being caught by those other guards (they all run on
  // already-in-cart lines, not on the value entering here). A negative weight is
  // deliberately still allowed -- receiptlessReturn2Enabled return lines use it
  // legitimately (see below) -- only a genuinely unparseable qty is rejected.
  if (!Number.isFinite(weight)) {
    console.error("[DATA-INTEGRITY] addProductToCart: rejected unparseable qty", JSON.stringify({ productId: addToCart?.productId, qty }));
    message.error(`${addToCart?.name || "Item"} could not be added — invalid quantity.`);
    return;
  }
  const userAccessController = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const layAwayFlag = useCartStore.getState().cart?.layAway || "N";
  const posConfig = usePOSConfigStore.getState().posConfig;
  const receiptlessReturn2Enabled = posConfig?.enableReceiptlessReturn2 === "Y";
  const liveCart = useCartStore.getState().cart || cart;
  const ObFlag = tillData.tillAccess.cwrTill.printTemplate.obController === "Y" ? true : false;
  const CWHardwareController = tillData.tillAccess.cwrTill.printTemplate.cwController === "Y" ? true : false;
  // let addedToCart = cart.items;
  const returnEnabled = receiptlessReturn2Enabled && cart?.isReturn === true && cart?.isBlindReceipt === true;
  // Read latest cart from Zustand (source of truth), not localStorage (stale mirror).
  // Deep-copy so mutations (unshift, property updates) don't corrupt Zustand's internal state.
  const activeCartFromStore = isExchange && !returnThisReceipt ? useCartStore.getState().exchangeCart || {} : useCartStore.getState().cart || cart;
  const activeCartFromStorage = JSON.parse(JSON.stringify(activeCartFromStore));
  let addedToCart = activeCartFromStorage.items || [];
  if (useTransactionStore.getState().selectedReturnType === "EXCHANGE") {
    trace("SMAddProduct", "addProductToCart entry", {
      productId: addToCart?.productId,
      qty,
      isExchange,
      returnThisReceipt,
      targetCart: isExchange && !returnThisReceipt ? "exchangeCart" : "cart",
      targetCartItemCount: addedToCart.length,
      addToCartIsReturn: addToCart?.isReturn,
    });
  }
  const itemsSnapshot = JSON.parse(JSON.stringify(addedToCart));
  const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  taxIncludeFlag = addToCart.taxFlag === "Y" ? "N" : taxIncludeFlag;
  const isWalletReturn = receiptlessReturn2Enabled && cart?.isReturn === true && cart?.isBlindReceipt === true;
  let expiryDiscount = useTransactionStore.getState().expiryDiscount === "Y";
  let deferredMsg = null;
  let deferredPoleDisplay = null;
  const cancelledItems = addedToCart.filter((p) => p.isCanceled === "Y");
  if (cancelledItems.length > 0) {
  }
  // BUG-1313 added a blanket !p.discountName guard so a plain re-add wouldn't merge into a
  // line carrying a TOTAL BILL discount (pricingRule.type === "TD"). discountName is also
  // stamped by every item-level pricing rule (BuyXPayYOfSame, QuantityDiscount, %/Flat,
  // Package, XGY, ExpiryDiscount, priceAdjustment) via the same field, so the blanket guard
  // caught those too: re-adding a product that already earned one of those on its first line
  // always created a duplicate, undiscounted line instead of incrementing the existing one
  // (BUG-2115, reported live on GPR). Only a genuine Total Bill Discount line should be
  // excluded here -- look up the rule that stamped it and check its type.
  const isTotalBillDiscountLine = (p) => Boolean(p.discountName) && findPricingRuleById(p.mPricingruleId)?.[0]?.type === "TD";
  // discountType is only ever stamped "XGY", exclusively by BuyXGetYDiscount.js, in two distinct
  // shapes of that rule: (1) a conditional "buy X get Y free" trigger, which genuinely SPLITS the
  // free units onto their own line -- that free line must stay excluded here, or a plain re-add
  // could get folded into it and misrepresent paid units as free; (2) the "All Y" config (every
  // configured product flagged isFree="Y", no separate trigger product), which BuyXGetYDiscount.js
  // deliberately mutates IN PLACE on the single existing line instead of splitting -- its own
  // comment there says the cart "must show one line per product... not a discounted-qty line plus
  // a separate full-price-qty line for the same product." Excluding that line here directly
  // contradicts that: it blocks the very merge the rule was written to guarantee. The two shapes
  // are told apart by the rule's own mPricingXProducts: a mix of isFree "Y"/"N" is the split-
  // required trigger shape; all "Y" (or no data available) is the All-Y single-line shape.
  const isSplitRequiredXgyLine = (p) => {
    if (p.discountType !== "XGY") return Boolean(p.discountType);
    const xProducts = findPricingRuleById(p.mPricingruleId)?.[0]?.mPricingXProducts;
    if (!Array.isArray(xProducts) || xProducts.length === 0) return true;
    return xProducts.some((x) => x.isFree === "N");
  };
  let index = addedToCart.findIndex(
    (p) =>
      p.productId === addToCart.productId &&
      (p.upc || "") === (addToCart.upc || "") &&
      p.mBatchId === addToCart.mBatchId &&
      p.uom === addToCart.uom &&
      p.isReturn === false &&
      // BUG-1610: must NOT match a cancelled line — matching it here restores/mutates the
      // cancelled record in place, erasing cancellation history from the cart (see the now-dead
      // isCanceled==="Y" restore branch below). Re-adding a cancelled product must fall through
      // to the "new line" branch instead, so the cancelled line stays visible and a fresh, distinct
      // active line is created. See project_bug1610_canceled_item_history_lost memory for the trace.
      p.order !== "Y" &&
      !expiryDiscount &&
      !isSplitRequiredXgyLine(p) &&
      !isTotalBillDiscountLine(p) &&
      p?.selectedAddons?.length <= 0
  );
  // BUG-2115 follow-up (live on GPR/RASBHARI, exposed by this same ticket's own fix):
  // the heuristic match above has no lineId in its criteria, so it always resolves to
  // the FIRST array element sharing productId/upc/batch/uom/etc -- fine while duplicate
  // lines with an identical key were rare, but addNewLine="Y" now creates them
  // routinely (that's its whole purpose). A qty-edit or delete on the 2nd/3rd such line
  // silently landed on the 1st one instead, because the caller-resolved lineId
  // (enterTotalQty/increaseProductQty/decreaseProductQty/deleteProduct all already
  // resolve the exact clicked line from the live cart before calling in) was never
  // consulted. Override with an exact lineId match when this call is an edit of an
  // existing line -- signaled by the same quantityChange/modified/deleteProductInCart
  // flags BUG-1300's addNewLine gate already uses to mean "resolve to that same line,
  // don't split." Deliberately NOT applied to a plain add (all three flags false): the
  // addon-repeat flow (AddonRepeatModal, index.js) intentionally reuses an existing
  // fired line's full object -- lineId included -- as a template to spawn a NEW
  // duplicate line, not edit the original; gating on these flags keeps that path on the
  // heuristic match above, unchanged. Left as the heuristic result if the lineId isn't
  // found (stale reference) -- never worse than before.
  if ((quantityChange || addToCart?.modified || deleteProductInCart) && addToCart.lineId) {
    const lineIdIndex = addedToCart.findIndex((p) => p.lineId === addToCart.lineId);
    if (lineIdIndex >= 0) {
      index = lineIdIndex;
    }
  }
  let weightFlag = false;
  let groupChildFlag = false;
  let groupParentFlag = false;
  let editedMixChildLineId = null;
  let isNewLineAdded = false;

  if (expiryDiscount === true || addToCart?.modified) {
    index = addedToCart.findIndex(
      (p) =>
        p.productId === addToCart.productId &&
        (p.upc || "") === (addToCart.upc || "") &&
        p.mBatchId === addToCart.mBatchId &&
        p.isReturn === false &&
        (expiryDiscount || addToCart?.modified) &&
        p.lineId === addToCart.lineId
    );
  }

  if (addedToCart[index]?.selectedAddons?.length > 0) {
    index = -1;
  }

  if (addedToCart[index]?.newCustomAttributes?.length > 0) {
    index = -1;
  }

  if (addToCart?.selectedAddons?.length > 0) {
    index = addedToCart.findIndex(
      (p) =>
        p.productId === addToCart.productId &&
        p.upc === addToCart.upc &&
        p.mBatchId === addToCart.mBatchId &&
        // p.isReturn === false &&
        p.lineId === addToCart.lineId &&
        p?.selectedAddons?.length > 0 &&
        p?.selectedAddons?.length === addToCart?.selectedAddons?.length
    );

    let indexFlag = true;
    addedToCart[index]?.selectedAddons?.forEach((addOn) => {
      // Compare with addToCart product add-ons
      let matchedAddOn = addToCart?.selectedAddons?.some((addOnItem) => {
        // return addOn.mProductAddOnId === addOnItem.mProductAddOnId;
        if (addOn.mProductAddOnId === addOnItem.mProductAddOnId) {
          addOn.weight = (addOn.weight ?? 0) + weight;
          return true;
        }
        return false;
      });

      // If there is no matching add-on, set flag to false
      if (!matchedAddOn) {
        indexFlag = false;
      }
    });
    if (!indexFlag) {
      index = -1;
    }
  } else if (addToCart?.productAddons?.length > 0 && addToCart.showAddons === "Y") {
    // BUG: productAddons is static master data (the size/variant choices a product CAN
    // offer) and is populated on every add regardless of whether the addon UI was ever
    // shown. Without the showAddons==="Y" gate here, ANY product configured with addon
    // options (even with showAddons==="N", addons never selected) fell into this branch
    // on every add. Matching only by lineId then always failed (a fresh add mints a new
    // lineId), forcing index=-1 and creating a duplicate line instead of merging into the
    // uom/batch-matched line already found above (line ~144) — reproduced live with
    // JAGGERY SUNNUNDALU / MOTICHUR LADDU (showAddons:"N") splitting into separate lines
    // on repeat adds of the same batch+UOM. showAddons==="Y" is the same gate already used
    // to decide whether to open the addon-selection modal at all (useCartHandlers.js:332,
    // ProductPanelDrawer.js, MenuProductGrid.js) — restricting this branch to that case
    // preserves its original intent (re-locate a line after an addon-modal submission)
    // without hijacking plain adds of addon-eligible-but-addon-disabled products.
    index = addedToCart.findIndex(
      (p) =>
        p.productId === addToCart.productId &&
        p.upc === addToCart.upc &&
        p.mBatchId === addToCart.mBatchId &&
        // p.isReturn === false &&
        p.lineId === addToCart.lineId
      // p?.selectedAddons?.length > 0 &&
      // p?.selectedAddons?.length === addToCart?.selectedAddons?.length
    );
  }

  if (addToCart?.newCustomAttributes?.length > 0) {
    index = addedToCart.findIndex(
      (p) =>
        p.productId === addToCart.productId &&
        p.upc === addToCart.upc &&
        p.mBatchId === addToCart.mBatchId &&
        p.isReturn === false &&
        p.lineId === addToCart.lineId &&
        p?.newCustomAttributes?.length > 0
    );
  }

  if (addToCart.isGiftCard === true) {
    index = addedToCart.findIndex((p) => p.productId === addToCart.productId && p.upc === addToCart.upc && p.lineId === addToCart.lineId);
  }

  if (addToCart.discountType || addToCart.discountName) {
    index = addedToCart.findIndex(
      (p) =>
        p.productId === addToCart.productId &&
        p.upc === addToCart.upc &&
        p.mBatchId === addToCart.mBatchId &&
        p.isReturn === false &&
        !expiryDiscount &&
        p.lineId === addToCart.lineId
    );
  }

  if (deleteProductInCart && index < 0 && addToCart.lineId) {
    index = addedToCart.findIndex((p) => p.lineId === addToCart.lineId && p.productId === addToCart.productId);
  }

  // BUG-2543: a delete must act on an existing line. When the record isn't a top-level
  // cart line (e.g. a Custom Mix child rebuilt without groupedItems), falling through
  // reached the new-line branch and unshifted a line with the negative delete qty.
  // groupedItems children are excluded: the groupedItems branch below locates them
  // inside their parent.
  if (deleteProductInCart && index < 0 && addToCart?.groupedItems !== true) {
    console.error(
      "[DATA-INTEGRITY] BUG-2543: delete target not found in cart, no line created",
      JSON.stringify({ productId: addToCart?.productId, lineId: addToCart?.lineId, qty })
    );
    message.error(`${addToCart?.name || "Item"} could not be removed — line not found in cart.`);
    return;
  }

  if (addToCart.discount <= 0) {
    delete addToCart.priority;
  }

  // Reverts TSK-3045's UOM-based narrowing (reported live on GPR, RASBHARI/10021):
  // TSK-3045 only forced a new line when the matched line's UOM differed from the
  // incoming add, so a same-UOM re-add of an addNewLine="Y" product silently merged
  // into the existing line instead of splitting -- indistinguishable from
  // addNewLine="N" for the common repeat-add case, since the base findIndex above
  // already requires equal UOM to match at all (a different-UOM add was never
  // reachable here in the first place). addNewLine="Y" must mean "always a new line"
  // regardless of UOM, per BUG-1299/1300's original intent -- restored to
  // unconditional here. The !deleteProductInCart/!quantityChange/!addToCart?.modified
  // guards from BUG-1300 are unchanged: qty edits, deletes, and in-place modifications
  // of an existing line must still resolve to that same line, not split.
  if (addToCart.addNewLine === "Y" && !deleteProductInCart && !quantityChange && !addToCart?.modified) {
    index = -1;
  }

  if (addToCart.productSegment === "CB" && (!addToCart.childItems || addToCart.childItems.length <= 0) && !deleteProductInCart) {
    // Open popup to select child items
    localStorage.setItem("productSegment", "CB");
    const itemsToDispatch = addToCart || [];
    localStorage.removeItem("productName");
    const { selectedItems = [] } = await new Promise((resolve) => {
      dispatchItemSelection(itemsToDispatch, cart, setCart, setProductStock, setShowCompleteSaleStock, setCompleteSaleStockData, (eventData) => {
        resolve({ selectedItems: eventData?.selectedItems || [] });
      });
    });
    localStorage.removeItem("productSegment");
    // Assign selected child items
    addToCart.childItems = selectedItems;
  }

  if (addToCart?.companion === "Y" && (!addToCart.children || addToCart.children.length <= 0)) {
    // Open popup to select child items
    localStorage.setItem("productSegment", "CP");
    const itemsToDispatch = addToCart || [];
    localStorage.removeItem("productName");
    const { selectedItems = [] } = await new Promise((resolve) => {
      dispatchItemSelection(itemsToDispatch, cart, setCart, setProductStock, setShowCompleteSaleStock, setCompleteSaleStockData, (eventData) => {
        resolve({ selectedItems: eventData?.selectedItems || [] });
      });
    });
    localStorage.removeItem("productSegment");
    // Assign selected child items
    addToCart.children = selectedItems;
  }

  if (addToCart?.groupedItems === true) {
    // BUG-2547: addToCart becomes the mix row below; keep the edited child to reselect it.
    editedMixChildLineId = addToCart.lineId;
    let cartObj = { ...cart };
    let selectedIndex;
    let items = cartObj?.items?.map((item, index) => {
      if (item?.children) {
        const updatedChildren = item.children.map((child) => {
          if (child.lineId === addToCart?.lineId) {
            selectedIndex = index;
            groupChildFlag = true;
            const stdTax = child.sunitprice * (1 + child.taxRate / 100);
            const grossStd = taxIncludeFlag === "Y" ? child.sunitprice : roundTo(stdTax, precision);
            const newQty = totalQtyFlag ? weight : addToCart?.weight + weight;
            const sp = parseFloat(child.sunitprice);
            const mrp = sp * newQty;
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + child.taxRate / 100) : (mrp * child.taxRate) / 100;
            const saleprice = (taxIncludeFlag === "Y" ? mrp : mrp + tax) - tax;
            const childnettotal = parseFloat(parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));
            const unitPrice = isFinite(childnettotal / newQty) ? (taxIncludeFlag === "Y" ? childnettotal / newQty / (1 + item.taxRate / 100) : saleprice) : 0;
            return {
              ...child,
              weight: newQty,
              actualWeight: newQty,
              // BUG-2547: baseQty is per unit of the mix (cart.js parent rescale and the cart
              // tables multiply it by the mix qty), not the child's total.
              baseQty: item.weight > 0 ? newQty / item.weight : newQty,
              linestd: roundTo(grossStd * newQty, precision),
              linegross: parseFloat(parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision)),
              linenet: childnettotal ? parseFloat(parseFloat(childnettotal - tax).toFixed(precision)) : 0,
              // netunit: childnettotal ? parseFloat(parseFloat(unitPrice).toFixed(precision)) : 0,
              salePrice: saleprice,
              // linestd: truncateToPrecision(grossStd * newQty, precision),
              // actualWeight: parseFloat((addToCart?.weight + weight / item?.weight).toFixed(2)),
            };
          }
          return child;
        });
        return {
          ...item,
          children: updatedChildren,
        };
      }
      return item;
    });
    addToCart = items[selectedIndex];
    addToCart?.children.forEach((childItem) => {
      // childItem.weight += weight;
      // let newWeight = totalQtyFlag
      //   ? parseFloat(truncateToPrecision(childItem.weight)) * parseFloat(truncateToPrecision(weight))
      //   : parseFloat(childItem.actualWeight * (selectedProductInCart.weight + weight));
      // childItem.weight = newWeight;
      const sp = parseFloat(childItem.sunitprice);
      const mrp = sp * childItem.weight;

      const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + childItem.taxRate / 100) : (mrp * childItem.taxRate) / 100;

      childItem.taxAmount = roundTo(tax, precision);
      childItem.nettotal = taxIncludeFlag === "Y" ? parseFloat(parseFloat(mrp).toFixed(precision)) : roundTo(mrp + tax, precision);
    });

    // Aggregate tax and net total from children
    const totalTax = sumBy(addToCart.children, "taxAmount");
    const totalNet = sumBy(addToCart.children, "nettotal");

    // BUG-2103 (line level): lodash sumBy accumulates raw, so a grouped/Custom Mix parent
    // inherited float noise from its children even though every child nettotal/taxAmount is
    // itself already rounded (cart.js:307/345/389/392). Noise removal only.
    addToCart.taxAmount = roundTo(totalTax, precision);
    addToCart.nettotal = roundTo(totalNet, precision);

    index = addedToCart.findIndex(
      (p) =>
        p.lineId === addToCart.lineId &&
        p.upc === addToCart.upc &&
        p.mBatchId === addToCart.mBatchId &&
        p.isReturn === false &&
        !expiryDiscount &&
        !p.discountType &&
        p?.selectedAddons?.length <= 0
    );

    addedToCart[index] = {
      ...addedToCart[index],
      ...addToCart,
      children: [...addToCart.children],
    };
  } else {
    if (addToCart?.children && addToCart?.children.length > 0 && addToCart.companion !== "Y") {
      addToCart?.children.forEach((childItem) => {
        // childItem.weight += weight;
        // let newWeight = totalQtyFlag
        //   ? parseFloat(truncateToPrecision(childItem.actualWeight)) * parseFloat(truncateToPrecision(weight))
        //   : parseFloat(childItem.actualWeight * ((selectedProductInCart.weight || addToCart.weight) + weight));
        let newWeight = totalQtyFlag
          ? parseFloat(truncateToPrecision(childItem.baseQty)) * parseFloat(truncateToPrecision(weight))
          : parseFloat(childItem.actualWeight * ((selectedProductInCart.weight || addToCart.weight) + weight));
        const stdTax = childItem.sunitprice * (1 + childItem.taxRate / 100);
        const grossStd = taxIncludeFlag === "Y" ? childItem.sunitprice : parseFloat(parseFloat(stdTax).toFixed(precision));
        childItem.weight = newWeight;
        // childItem.linestd = truncateToPrecision(grossStd * newWeight, precision);
        childItem.linestd = grossStd * newWeight;
        const sp = parseFloat(childItem.sunitprice);
        const mrp = sp * childItem.weight;
        const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + childItem.taxRate / 100) : (mrp * childItem.taxRate) / 100;
        const childnettotal = parseFloat(parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));
        ((childItem.linegross = childnettotal), (childItem.linenet = childnettotal ? parseFloat(parseFloat(childnettotal - tax).toFixed(precision)) : 0));
        childItem.taxAmount = tax;
        childItem.nettotal = childnettotal;
      });

      // Aggregate tax and net total from children
      const totalTax = sumBy(addToCart.children, "taxAmount");
      const totalNet = sumBy(addToCart.children, "nettotal");

      // BUG-2103 (line level): same raw sumBy aggregation as the branch above.
      addToCart.taxAmount = roundTo(totalTax, precision);
      addToCart.nettotal = roundTo(totalNet, precision);

      addedToCart[index] = {
        ...addedToCart[index],
        ...addToCart,
        children: [...addToCart.children],
      };
      groupParentFlag = true;
    }
  }
  if (addToCart.childItems && addToCart.childItems.length > 0) {
    const updatedChildItems = addToCart.childItems.map((childItem) => {
      const sp = parseFloat(childItem.sunitprice);
      const mrp = sp * childItem.weight;
      const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + childItem.taxRate / 100) : (mrp * childItem.taxRate) / 100;
      const newWeight = totalQtyFlag
        ? parseFloat(truncateToPrecision(childItem.rawWeight)) * parseFloat(truncateToPrecision(weight))
        : parseFloat(childItem?.rawWeight * ((selectedProductInCart.weight || addToCart.weight) + weight));
      const nettotal = parseFloat((taxIncludeFlag === "Y" ? mrp : mrp + tax).toFixed(precision));

      return {
        ...childItem,
        taxAmount: tax,
        weight: newWeight,
        nettotal,
      };
    });

    // Update addToCart values
    addToCart.childItems = updatedChildItems;
    addToCart.salePrice = updatedChildItems.reduce((total, child) => {
      const sp = parseFloat(child.sunitprice);
      const mrp = sp * child.weight;
      const tax = child.taxAmount;
      return total + (taxIncludeFlag === "Y" ? mrp : mrp + tax) - tax;
    }, 0);

    addToCart.sunitprice = addToCart.salePrice;
    addToCart.realPrice = addToCart.salePrice;
    addToCart.originalPrice = addToCart.salePrice;

    if (index >= 0) {
      addedToCart[index] = {
        ...addedToCart[index],
        ...addToCart,
        childItems: [...updatedChildItems],
      };
    }
  }

  // BUG-1692: a Buy-X-Get-Y promotion on a weight-based product fragments one logical product
  // into multiple cart lines (paid chunks + discounted chunks + a leftover remainder), all
  // sharing productId/mBatchId/uom under different lineIds. That remainder is only ever spliced
  // out when its weight is EXACTLY 0 (see the XGY split logic in BuyXGetYDiscount.js), which a
  // scale-weighed total (e.g. 4.001kg) almost never is — so it regenerates on every recompute
  // and the product never fully leaves the cart no matter which fragment gets deleted. When the
  // user deletes a fragment of such a product, remove every fragment sharing that product's
  // identity in this SAME pass — one splice batch, one setCart, one message — instead of just
  // the clicked lineId, so a single delete click removes the whole product. addNewLine="Y"
  // lines (BUG-1299/1300, intentionally independent duplicate lines) and parked/composite
  // (addon/child-bundle) lines are excluded from this matching so they're never swept in.
  //
  // IMPORTANT: the trigger signal is discountType === "XGY" ONLY, never mPricingruleId alone.
  // mPricingruleId is stamped by five different promo types (QuantityDiscount, priceAdjustment,
  // BuyXPayYOfSame, PackageDiscount, BuyXGetYDiscount) and none of the first four ever fragment a
  // line — they apply a discount to a single line in place. Only BuyXGetYDiscount ever sets
  // discountType to "XGY" (see BuyXGetYDiscount.js:170/285), exclusively on the free/discounted
  // fragment it creates. Triggering on mPricingruleId alone would wrongly cascade-delete two
  // genuinely independent lines of the same product that each merely earned their own unrelated
  // discount (e.g. adding the same product twice where each line independently qualifies for a
  // plain QuantityDiscount) — confirmed as a real false-positive risk, not a hypothetical one.
  const isSameProductLine = (item, ref) =>
    item.productId === ref.productId &&
    item.mBatchId === ref.mBatchId &&
    item.isReturn === ref.isReturn &&
    item.uom === ref.uom &&
    item.addNewLine !== "Y" &&
    !item.parkedItem &&
    !(item.selectedAddons?.length > 0) &&
    !(item.childItems?.length > 0) &&
    !(item.children?.length > 0);
  const isPromoFragmentDelete =
    deleteProductInCart &&
    index >= 0 &&
    addedToCart[index].addNewLine !== "Y" &&
    !addedToCart[index].parkedItem &&
    (addedToCart[index].discountType === "XGY" ||
      addedToCart.some((item) => item.lineId !== addedToCart[index].lineId && isSameProductLine(item, addedToCart[index]) && item.discountType === "XGY"));

  if (isPromoFragmentDelete) {
    const refItem = addedToCart[index];
    const deletedProductName = refItem.name;
    for (let i = addedToCart.length - 1; i >= 0; i -= 1) {
      if (isSameProductLine(addedToCart[i], refItem)) {
        addedToCart.splice(i, 1);
      }
    }
    setSelectedProductInCart({});
    deferredMsg = { type: "warning", text: `${deletedProductName} ${messages.errors.cart.removed_successfully}` };
  } else if (index >= 0 && !addedToCart?.[index]?.parkedItem && !addedToCart?.[index]?.discountType) {
    const isQtyDecimal = addToCart.isQtyDesimal;
    let newWeight = totalQtyFlag ? parseFloat(weight) : parseFloat(addedToCart[index].weight) + parseFloat(weight);
    // Round to UOM precision so 1 + 1.22 doesn't become 2.2200000000000002 in
    // the cell (issue #12). Prefer the UOM stdprecision when present, falling
    // back to currency precision so we don't accidentally truncate decimal-by-
    // weight items more aggressively than they're priced.
    const qtyPrecision = parseInt(addToCart?.uomData?.[0]?.stdprecision, 10);
    const roundPrec = !isNaN(qtyPrecision) ? qtyPrecision : precision;
    if (Number.isFinite(newWeight)) {
      newWeight = parseFloat(newWeight.toFixed(roundPrec));
    }

    if (groupChildFlag === false) {
      addedToCart[index].weight = newWeight;
      addToCart.weight = newWeight;
    }

    if (!(parseFloat(addedToCart[index].weight) > 0)) {
      // BUG-2041: parseFloat(qty) on an unparseable quantity input (e.g. an emptied
      // qty box) produces NaN, and `NaN <= 0` is false -- the old check let a NaN
      // line silently survive here instead of being caught by this same invalid-qty
      // handling that already exists for zero/negative. `!(x > 0)` is true for NaN,
      // 0, and negatives alike (and identical to the old `x <= 0` for every real
      // number), so this closes the gap without changing behavior for any valid qty.
      weightFlag = true;
      setSelectedProductInCart({});
      let setAuthTokens;

      if (addToCart.isGiftCard || addedToCart[index]?.iscoupon) {
        const authHeaders = await getOAuthHeaders();
        if (authHeaders && authHeaders.access_token) {
          setAuthTokens = authHeaders.access_token;
        }
      }

      if (addedToCart[index]?.iscoupon) {
        await discardCouponService(addedToCart[index].couponInput, addedToCart[index].referenceId);
        // const discardCoupon = {
        //   query: `query{
        //       discardCoupon(couponcode:"${addedToCart[index].couponInput}",referenceId: "${addedToCart[index].referenceId}"){
        //           status
        //           message
        //       }
        //   }`,
        // };
        // Axios({
        //   url: serverUrl,
        //   method: "POST",
        //   data: discardCoupon,
        //   headers: {
        //     "Content-Type": "Application/json",
        //     Authorization: `${setAuthTokens}`,
        //   },
        // });
      }
      if (cart.couponInput?.length > 0) {
        cart.couponInput = cart.couponInput?.filter((coupon) => {
          if (addedToCart[index].couponInput !== coupon) {
            return coupon;
          }
        });
      }

      // if (addToCart.selectedAddons.length > 0) {
      //   checkAndShowAddOnModal(addToCart.selectedAddons);
      // }

      if (addToCart.isGiftCard) {
        let giftCardDataIndex = cart?.giftCardData?.findIndex((giftCard) => giftCard.number === addToCart.cardNo);
        // const discardGiftCard = {
        //   query: `mutation{
        //             discardGiftCard(giftCards: [{cardNo: "${cart.giftCardData[giftCardDataIndex].number}" , referenceNo : "${cart.giftCardData[giftCardDataIndex].refId}"}]){
        //               status
        //               message
        //       }
        //   }`,
        // };
        // Axios({
        //   url: serverUrl,
        //   method: "POST",
        //   data: discardGiftCard,
        //   headers: {
        //     "Content-Type": "Application/json",
        //     Authorization: `${setAuthTokens}`,
        //   },
        // }).then((res) => {
        //   cart.giftCardData.splice(giftCardDataIndex, 1);
        // });
        try {
          await discardGiftCard(activeCartFromStorage.giftCardData[giftCardDataIndex].number, activeCartFromStorage.giftCardData[giftCardDataIndex].refId);

          // Remove the gift card via filter on the deep-copy — avoids mutating Zustand state directly
          activeCartFromStorage.giftCardData = activeCartFromStorage.giftCardData.filter((_, i) => i !== giftCardDataIndex);
        } catch (error) {
          console.error("Error discarding gift card:", error);
          message.error(`${messages.errors.cart.gift_card_discard_failed}`);
        }
      }
      message.warning(`${addToCart.name} ${messages.errors.cart.removed_successfully}`, 0.1);
      if (cart.advancePayment <= 0 || !cart.advancePayment) {
        // BUG-2151: this used to filter cart.manualDiscountData.lineLevelDiscount -- `cart` is a
        // stale reference distinct from activeCartFromStorage (deep-cloned at the top of this
        // function), and it's activeCartFromStorage that actually feeds cartObj/the persisted
        // cart state below. Filtering the wrong object left the deleted line's manual-discount
        // bookkeeping entry alive in the real cart state, keyed only by productId -- any later
        // discount replay (removeAllDiscounts, on customer attach/remove or coupon removal)
        // then silently reapplied it to a freshly re-added line of the same product.
        if (activeCartFromStorage?.manualDiscountData?.lineLevelDiscount?.length > 0) {
          activeCartFromStorage.manualDiscountData.lineLevelDiscount = activeCartFromStorage.manualDiscountData.lineLevelDiscount.filter(
            (lineItem) => lineItem.productId !== addedToCart[index].productId
          );
        }
        addedToCart.splice(index, 1);
      } else {
        addedToCart[index].nettotal = 0;
        addedToCart[index].unitTax = 0;
        addedToCart[index].netStd = 0;
        // BUG-2208: linegross/linenet/linetax etc. are what actually ships to the backend
        // sync payload (useOrderProcessing.js reads item.linegross directly when
        // disableOfflineBilling !== "Y") -- nettotal alone being 0 doesn't stop the stale
        // pre-removal amount from surviving on this now-weight-0 line and blowing the
        // header/line total match on sync.
        addedToCart[index].linegross = 0;
        addedToCart[index].linenet = 0;
        addedToCart[index].linetax = 0;
        addedToCart[index].linestd = 0;
        addedToCart[index].grosslist = 0;
        // Both castings exist in the wild: cart.js's own recalc Object.assign derives
        // `netList`/`listprice`, but a retrieved order's line (useOrderHistory.js) is
        // stamped `netlist`/`listPrice` from the raw server response -- zero both so
        // whichever one this item actually carries doesn't survive as a stale value.
        addedToCart[index].netList = 0;
        addedToCart[index].netlist = 0;
        addedToCart[index].grossunit = 0;
        addedToCart[index].grossstd = 0;
        addedToCart[index].netunit = 0;
        addedToCart[index].listprice = 0;
        addedToCart[index].listPrice = 0;
      }
    } else {
      // BUG-2318: snapshot the price-relevant fields before this edit touches them, so a
      // non-finite result below can be reverted in place instead of committing NaN to the cart.
      const preEditSnapshot2318 = {
        discount: addedToCart[index].discount,
        allDiscounts: addedToCart[index].allDiscounts,
        taxAmount: addedToCart[index].taxAmount,
        nettotal: addedToCart[index].nettotal,
        modified: addedToCart[index].modified,
      };
      let manualDiscountInput = 0;
      if (addedToCart[index].discountType === "PD") {
        manualDiscountInput = addedToCart[index].discountValue;
      }
      const discountAmt = (parseFloat(manualDiscountInput) / 100) * parseFloat(addedToCart[index].realPrice);
      addedToCart[index].discount = totalQtyFlag ? discountAmt * weight : discountAmt * addedToCart[index].weight;
      dropAppliedDiscountAmounts(addedToCart[index]);
      //const addOnsNewProduct = sumBy(addedToCart?.selectedAddons, "price");
      const addOnsPriceSum = sumBy(addedToCart[index]?.selectedAddons, "price");
      const sp = parseFloat(addedToCart[index].realPrice) + addOnsPriceSum - discountAmt;
      //let mrp = totalQtyFlag ? parseFloat(sp) * weight : parseFloat(sp) * addedToCart[index].weight;
      //const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + addedToCart[index].taxRate / 100) : (mrp * addedToCart[index].taxRate) / 100;
      if (groupChildFlag === false && groupParentFlag === false) {
        const factor = Math.pow(10, precision);
        const qty = addedToCart[index].weight;
        // BUG-1674: compute tax/mrp on the full line (sp * qty) and round once — rounding
        // the per-unit tax before multiplying by qty amplifies the rounding error by qty
        // (e.g. unit tax 1.905 -> rounds to 1.91 -> x100 qty = +0.50 vs the correct total).
        const lineMrp = sp * qty;
        const lineTax = taxIncludeFlag === "Y" ? lineMrp - lineMrp / (1 + addedToCart[index].taxRate / 100) : (lineMrp * addedToCart[index].taxRate) / 100;
        const roundedLineTax = Math.round((lineTax + Number.EPSILON) * factor) / factor;
        const roundedLineMrp = Math.round((lineMrp + Number.EPSILON) * factor) / factor;

        addedToCart[index].taxAmount = roundedLineTax;
        // BUG-2103 (line level): roundedLineMrp and roundedLineTax are each already rounded
        // to precision, but their SUM was not -- 70.29 + 3.51 === 73.80000000000001. That fed
        // linegross, which ships to the backend as the invoice line amount, so a line could
        // carry more decimals than the currency has. Rounding the sum shifts the value by ~1e-14
        // and cannot move a paise. cart.js:864 (the third sibling of this calculation) has always
        // done exactly this; these two were the outliers.
        addedToCart[index].nettotal = roundTo(taxIncludeFlag === "Y" ? roundedLineMrp : roundedLineMrp + roundedLineTax, precision);
      }
      // BUG-2318: mirrors the existing new-line guard (cart.js's `[DATA-INTEGRITY] ...
      // rejected new line` check) -- a qty edit on a line whose realPrice is missing/invalid
      // (e.g. a Custom Mix child never stamped with realPrice, see useParkedBills.js) computes
      // sp/nettotal/taxAmount as NaN here. Revert this line's price fields to their pre-edit
      // values instead of letting NaN reach the cart total and the Payment Panel.
      if (!Number.isFinite(addedToCart[index].nettotal) || !Number.isFinite(addedToCart[index].taxAmount)) {
        console.error(
          "[DATA-INTEGRITY] BUG-2318: qty-edit produced non-finite total/tax, reverting line",
          JSON.stringify({
            productId: addedToCart[index].productId,
            name: addedToCart[index].name,
            realPrice: addedToCart[index].realPrice,
            salePrice: addedToCart[index].salePrice,
            weight: addedToCart[index].weight,
            nettotal: addedToCart[index].nettotal,
            taxAmount: addedToCart[index].taxAmount,
          })
        );
        Object.assign(addedToCart[index], preEditSnapshot2318);
        deferredMsg = { type: "error", text: `${addToCart.name || "Item"} quantity could not be updated — invalid price data.` };
      } else {
        addedToCart[index].modified = addToCart?.modified ? true : false;
        delete addedToCart[index].nextRule;
        if (addedToCart[index].discount <= 0) {
          delete addedToCart[index].priority;
        }
        deferredMsg = {
          type: qty < 0 ? "warning" : "success",
          text: `${addToCart.name} ${qty < 0 ? `${messages.errors.cart.removed_successfully}` : `${messages.errors.cart.added_successfully}`}`,
        };
      }
    }
  } else if (index >= 0 && !addedToCart?.[index]?.parkedItem && addedToCart?.[index]?.discountType) {
    if (groupChildFlag === false) {
      addedToCart[index].weight = totalQtyFlag
        ? parseFloat(truncateToPrecision(weight, addToCart.isQtyDesimal))
        : parseFloat(truncateToPrecision(addedToCart[index].weight, addToCart.isQtyDesimal)) + parseFloat(parseFloat(truncateToPrecision(weight, addToCart.isQtyDesimal)));
      addToCart.weight = parseFloat(truncateToPrecision(addedToCart[index].weight, addToCart.isQtyDesimal));
    }
    if (!(parseFloat(addedToCart[index].weight) > 0)) {
      // BUG-2041 follow-up: this discounted-line quantity-edit branch used `=== 0` as its
      // invalid-quantity check, which is NaN-blind (`NaN === 0` is false) -- an unparseable
      // qty edit on an already-discounted line slipped past this check and flowed into the
      // discount/taxAmount/nettotal math in the else branch below, the same JSON-null-on-
      // serialize symptom BUG-2041/2044 fixed for the two other quantity-edit branches
      // (cart.js:517, :770). `!(x > 0)` is true for NaN, 0, and negatives alike, and is
      // identical to the old `x === 0` check for every zero/positive real number.
      setSelectedProductInCart({});
      message.warning(`${addToCart.name} ${messages.errors.cart.removed_successfully}`, 0.1);
      if (cart.advancePayment <= 0) {
        // Same stale-`cart`-reference fix as the non-discountType delete branch above.
        if (activeCartFromStorage?.manualDiscountData?.lineLevelDiscount?.length > 0) {
          activeCartFromStorage.manualDiscountData.lineLevelDiscount = activeCartFromStorage.manualDiscountData.lineLevelDiscount.filter(
            (lineItem) => lineItem.productId !== addedToCart[index].productId
          );
        }
        addedToCart.splice(index, 1);
      } else {
        addedToCart[index].nettotal = 0;
        addedToCart[index].unitTax = 0;
        addedToCart[index].netStd = 0;
        addedToCart[index].salePrice = 0;
        // BUG-2208: see the identical zeroing in the non-discounted branch above -- same leak,
        // this is the discounted-line quantity-edit-to-zero branch.
        addedToCart[index].linegross = 0;
        addedToCart[index].linenet = 0;
        addedToCart[index].linetax = 0;
        addedToCart[index].linestd = 0;
        addedToCart[index].grosslist = 0;
        // Both castings exist in the wild: cart.js's own recalc Object.assign derives
        // `netList`/`listprice`, but a retrieved order's line (useOrderHistory.js) is
        // stamped `netlist`/`listPrice` from the raw server response -- zero both so
        // whichever one this item actually carries doesn't survive as a stale value.
        addedToCart[index].netList = 0;
        addedToCart[index].netlist = 0;
        addedToCart[index].grossunit = 0;
        addedToCart[index].grossstd = 0;
        addedToCart[index].netunit = 0;
        addedToCart[index].listprice = 0;
        addedToCart[index].listPrice = 0;
      }
    } else {
      // BUG-2318: same snapshot-and-revert guard as the non-discounted qty-edit branch above.
      const preEditSnapshot2318b = {
        discount: addedToCart[index].discount,
        allDiscounts: addedToCart[index].allDiscounts,
        taxAmount: addedToCart[index].taxAmount,
        nettotal: addedToCart[index].nettotal,
        modified: addedToCart[index].modified,
      };
      let manualDiscountInput = 0;
      if (addedToCart[index].discountType === "PD") {
        manualDiscountInput = addedToCart[index].discountValue;
      }
      const discountAmt = (parseFloat(manualDiscountInput) / 100) * parseFloat(addedToCart[index].realPrice);
      addedToCart[index].discount = totalQtyFlag ? discountAmt * weight : discountAmt * addedToCart[index].weight;
      dropAppliedDiscountAmounts(addedToCart[index]);
      const addOnsPriceSum = sumBy(addedToCart[index]?.selectedAddons, "price");
      const sp = parseFloat(addedToCart[index].realPrice) + addOnsPriceSum - discountAmt;
      if (groupChildFlag === false && groupParentFlag === false) {
        const factor = Math.pow(10, precision);
        const qty = addedToCart[index].weight;
        // BUG-1674: compute tax/mrp on the full line (sp * qty) and round once — rounding
        // the per-unit tax before multiplying by qty amplifies the rounding error by qty
        // (e.g. unit tax 1.905 -> rounds to 1.91 -> x100 qty = +0.50 vs the correct total).
        const lineMrp = sp * qty;
        const lineTax = taxIncludeFlag === "Y" ? lineMrp - lineMrp / (1 + addedToCart[index].taxRate / 100) : (lineMrp * addedToCart[index].taxRate) / 100;
        const roundedLineTax = Math.round((lineTax + Number.EPSILON) * factor) / factor;
        const roundedLineMrp = Math.round((lineMrp + Number.EPSILON) * factor) / factor;

        addedToCart[index].taxAmount = roundedLineTax;
        // BUG-2103 (line level): roundedLineMrp and roundedLineTax are each already rounded
        // to precision, but their SUM was not -- 70.29 + 3.51 === 73.80000000000001. That fed
        // linegross, which ships to the backend as the invoice line amount, so a line could
        // carry more decimals than the currency has. Rounding the sum shifts the value by ~1e-14
        // and cannot move a paise. cart.js:864 (the third sibling of this calculation) has always
        // done exactly this; these two were the outliers.
        addedToCart[index].nettotal = roundTo(taxIncludeFlag === "Y" ? roundedLineMrp : roundedLineMrp + roundedLineTax, precision);
      }
      // BUG-2318: see the identical guard in the non-discounted qty-edit branch above for the
      // full explanation -- revert instead of committing a NaN nettotal/taxAmount to the cart.
      if (!Number.isFinite(addedToCart[index].nettotal) || !Number.isFinite(addedToCart[index].taxAmount)) {
        console.error(
          "[DATA-INTEGRITY] BUG-2318: qty-edit (discounted line) produced non-finite total/tax, reverting line",
          JSON.stringify({
            productId: addedToCart[index].productId,
            name: addedToCart[index].name,
            realPrice: addedToCart[index].realPrice,
            salePrice: addedToCart[index].salePrice,
            weight: addedToCart[index].weight,
            nettotal: addedToCart[index].nettotal,
            taxAmount: addedToCart[index].taxAmount,
          })
        );
        Object.assign(addedToCart[index], preEditSnapshot2318b);
        deferredMsg = { type: "error", text: `${addToCart.name || "Item"} quantity could not be updated — invalid price data.` };
      } else {
        addedToCart[index].modified = addToCart?.modified ? true : false;
        delete addedToCart[index].nextRule;
        if (addedToCart[index].discount <= 0) {
          delete addedToCart[index].priority;
        }
        deferredMsg = {
          type: qty < 0 ? "warning" : "success",
          text: `${addToCart.name} ${qty < 0 ? `${messages.errors.cart.removed_successfully}` : `${messages.errors.cart.added_successfully}`}`,
        };
      }
    }
  } else if (index >= 0 && addedToCart?.[index]?.parkedItem) {
    if (addedToCart[index].isCanceled === "Y") {
      if (!(parseFloat(weight) > 0)) {
        // BUG-2041 follow-up: this cancel-restore branch (re-firing a previously cancelled
        // line) had NO invalid-quantity guard at all -- an unparseable weight (NaN) flowed
        // straight into mrp/tax/nettotal below and serialized as JSON null downstream, the
        // same root cause BUG-2041/2044 fixed for the other quantity-edit branches (cart.js
        // ~517, ~770). Unlike those branches, there's nothing safe to splice here -- the
        // line is still isCanceled:"Y" at this point -- so bail out and leave it cancelled
        // instead of restoring it with a corrupted quantity.
        deferredMsg = { type: "warning", text: `${addedToCart[index].name}: Invalid quantity, item not restored` };
      } else {
        addedToCart[index].isCanceled = "N";
        addedToCart[index].order = "N";
        addedToCart[index].firedQty = 0;
        addedToCart[index].weight = parseFloat(weight);
        addedToCart[index].sendQty = parseFloat(weight);
        addedToCart[index].update = "N";
        const addOnsPriceSum = sumBy(addedToCart[index]?.selectedAddons, "price") || 0;
        // Fall back to salePrice if realPrice is missing — avoids NaN propagating into nettotal/grossunit/subTotal.
        const sp = parseFloat(addedToCart[index].realPrice ?? addedToCart[index].salePrice) + addOnsPriceSum;
        const mrp = sp * addedToCart[index].weight;
        const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + addedToCart[index].taxRate / 100) : (mrp * addedToCart[index].taxRate) / 100;
        addedToCart[index].taxAmount = parseFloat(parseFloat(tax).toFixed(precision));
        addedToCart[index].nettotal = taxIncludeFlag === "Y" ? parseFloat(mrp.toFixed(precision)) : parseFloat((mrp + tax).toFixed(precision));
        addedToCart[index].discount = 0;
        addToCart = { ...addedToCart[index] };
        deferredMsg = { type: "success", text: `${addedToCart[index].name} ${messages.errors.cart.added_successfully}` };

        // Persist the restoration to fbOrderData (cart.items + wsData.items), mirroring the DB-write
        // pattern used by the non-cancelled parked-item branch below. Without this, reselecting this
        // table (handleExistingOrder) rebuilds the cart from the stale wsData snapshot, which still
        // marks this line isCanceled:"Y", silently re-cancelling the just-restored item. (BUG-1504)
        try {
          const allFbOrdersRestore = await db.fbOrderData.toArray();
          const matchingOrderRestore = allFbOrdersRestore.find((order) => order.cart?.sOrderID === cart.sOrderID && order.table === cart.tableNo);
          if (matchingOrderRestore) {
            const restoredLineId = addedToCart[index].lineId;
            const restoredFbOrderLineId = addedToCart[index].fbOrderLineId;
            const updatedItemsRestore = (matchingOrderRestore.cart?.items || []).map((item) =>
              item.lineId === restoredLineId ? { ...addedToCart[index] } : item
            );
            const hasWsData = matchingOrderRestore.wsData && Object.keys(matchingOrderRestore.wsData).length > 0;
            const updatedOrderRestore = {
              ...matchingOrderRestore,
              cart: { ...matchingOrderRestore.cart, items: updatedItemsRestore },
            };
            if (hasWsData) {
              updatedOrderRestore.wsData = {
                ...matchingOrderRestore.wsData,
                items: (matchingOrderRestore.wsData.items || []).map((item) =>
                  item.fbOrderLineId === restoredFbOrderLineId ? { ...item, isCanceled: "N", orderStatus: "OR" } : item
                ),
              };
            }
            await db.fbOrderData.put(updatedOrderRestore);
          }
        } catch (error) {
          console.error("[BUG-1504] Error persisting restored item to fbOrderData:", error);
        }
      }
    } else {
    const isQtyDecimal = addToCart.isQtyDesimal;
    let newWeight = totalQtyFlag ? parseFloat(weight) : parseFloat(addedToCart[index].weight) + parseFloat(weight);
    let sendQty = parseFloat(addedToCart[index]?.sendQty || 0) + parseFloat(weight);
    // BUG-1864: this parked-item-restore branch was accumulating weight with no rounding at all,
    // unlike the equivalent non-parked branch above (~line 494) — round to the product's own qty
    // precision so float residue can't survive as a decimal qty on a unit-based product.
    const qtyPrecision = parseInt(addToCart?.uomData?.[0]?.stdprecision ?? isQtyDecimal, 10);
    const roundPrec = !isNaN(qtyPrecision) ? qtyPrecision : precision;
    if (Number.isFinite(newWeight)) {
      newWeight = parseFloat(newWeight.toFixed(roundPrec));
    }
    if (groupChildFlag === false) {
      addedToCart[index].weight = newWeight;
      addToCart.weight = newWeight;
      addedToCart[index] = {
        ...addedToCart[index],
        sendQty: sendQty,
      };
    }

    if (!(parseFloat(addedToCart[index].weight) > 0)) {
      // BUG-2041: same NaN-blind gap as the non-parked branch above (cart.js:517) --
      // a parked/restored item's quantity edit goes through the same
      // parseFloat(qty)-with-no-validation path (weight/newWeight above), so it needs
      // the same NaN-safe guard. See that branch's comment for the full explanation.
      addedToCart.splice(index, 1);
      if (!isExchange) {
        setCart((prevCart) => {
          const updatedItems = prevCart.items.filter((cartItem) => cartItem?.lineId !== addedToCart?.[index]?.lineId);
          const updatedCart = { ...prevCart, addedToCart: updatedItems };
          return updatedCart;
        });
      }

      try {
        const tableData = await db.tableData.toArray();
        const updatedTableData = tableData.map((table) => {
          if (table.title === cart.tableNo) {
            return { ...table, items: [...cart.items] };
          }
          return table;
        });
        for (const updatedTable of updatedTableData) {
          if (updatedTable.title === cart.tableNo) {
            await db.tableData.put(updatedTable);
          }
        }
        setTableCards(updatedTableData);
      } catch (error) {
        console.error("Error updating table data:", error);
      }

      try {
        // Fetch all FB orders and filter in JS — closure-based .filter() breaks in SQLite/IPC mode
        const allFbOrders1 = await db.fbOrderData.toArray();
        const matchingOrder = allFbOrders1.find((order) => order.cart?.sOrderID === cart.sOrderID && order.table === cart.tableNo);

        if (matchingOrder) {
          const updatedOrder = {
            ...matchingOrder,
            cart: {
              ...matchingOrder.cart,
              items: [...cart.items],
            },
          };
          await db.fbOrderData.put(updatedOrder);
        }
      } catch (error) {
        console.error("Error updating fbOrderData:", error);
      }
    } else {
      // BUG-2318: same snapshot-and-revert guard as the two Retail qty-edit branches above.
      const preEditSnapshot2318c = {
        discount: addedToCart[index].discount,
        allDiscounts: addedToCart[index].allDiscounts,
        taxAmount: addedToCart[index].taxAmount,
        nettotal: addedToCart[index].nettotal,
        modified: addedToCart[index].modified,
      };
      let manualDiscountInput = 0;
      if (addedToCart[index].discountType === "PD") {
        manualDiscountInput = addedToCart[index].discountValue;
      }
      const discountAmt = (parseFloat(manualDiscountInput) / 100) * parseFloat(addedToCart[index].realPrice);
      addedToCart[index].discount = totalQtyFlag ? discountAmt * weight : discountAmt * addedToCart[index].weight;
      dropAppliedDiscountAmounts(addedToCart[index]);
      //const addOnsNewProduct = sumBy(addedToCart?.selectedAddons, "price");
      const addOnsPriceSum = sumBy(addedToCart[index]?.selectedAddons, "price");
      const sp = parseFloat(addedToCart[index].realPrice) + addOnsPriceSum - discountAmt;
      let mrp = totalQtyFlag ? parseFloat(sp) * weight : parseFloat(sp) * addedToCart[index].weight;
      const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + addedToCart[index].taxRate / 100) : (mrp * addedToCart[index].taxRate) / 100;
      if (groupChildFlag === false && groupParentFlag === false) {
        const factor = Math.pow(10, precision);
        const qty = addedToCart[index].weight;
        const unitTax = taxIncludeFlag === "Y" ? sp - sp / (1 + addedToCart[index].taxRate / 100) : (sp * addedToCart[index].taxRate) / 100;

        const unitMrp = sp;
        const roundedUnitTax = Math.round((unitTax + Number.EPSILON) * factor) / factor;
        const roundedUnitMrp = Math.round((unitMrp + Number.EPSILON) * factor) / factor;

        addedToCart[index].taxAmount = taxIncludeFlag === "Y" ? parseFloat(parseFloat(tax).toFixed(precision)) : roundTo(tax, precision);
        addedToCart[index].nettotal =
          taxIncludeFlag === "Y"
            ? parseFloat(parseFloat(mrp).toFixed(precision))
            : // parseFloat(parseFloat(mrp + tax).toFixed(precision));
              roundTo(mrp + tax, precision);
      }
      // BUG-2318: see the identical guard in the non-discounted qty-edit branch (cart.js
      // ~line 715) for the full explanation -- revert instead of committing a NaN
      // nettotal/taxAmount to the cart. No early return here, same as the other two
      // branches -- the table/fbOrderData sync below still runs, just on the now-reverted
      // (valid) line, which is harmless.
      if (!Number.isFinite(addedToCart[index].nettotal) || !Number.isFinite(addedToCart[index].taxAmount)) {
        console.error(
          "[DATA-INTEGRITY] BUG-2318: qty-edit (Restaurant parked item) produced non-finite total/tax, reverting line",
          JSON.stringify({
            productId: addedToCart[index].productId,
            name: addedToCart[index].name,
            realPrice: addedToCart[index].realPrice,
            salePrice: addedToCart[index].salePrice,
            weight: addedToCart[index].weight,
            nettotal: addedToCart[index].nettotal,
            taxAmount: addedToCart[index].taxAmount,
          })
        );
        Object.assign(addedToCart[index], preEditSnapshot2318c);
        deferredMsg = { type: "error", text: `${addToCart.name || "Item"} quantity could not be updated — invalid price data.` };
      } else {
        addedToCart[index].modified = addToCart?.modified ? true : false;
        delete addedToCart[index].nextRule;
        if (addedToCart[index].discount <= 0) {
          delete addedToCart[index].priority;
        }
        deferredMsg = {
          type: qty < 0 ? "warning" : "success",
          text: `${addToCart.name} ${qty < 0 ? messages.errors.cart.removed_successfully : messages.errors.cart.added_successfully}`,
        };
      }
      try {
        const tableData = await db.tableData.toArray();
        const updatedTableData = tableData.map((table) => {
          if (table.title === cart.tableNo) {
            return { ...table, items: [...cart.items] };
          }
          return table;
        });
        for (const updatedTable of updatedTableData) {
          if (updatedTable.title === cart.tableNo) {
            await db.tableData.put(updatedTable);
          }
        }
        setTableCards(updatedTableData);
      } catch (error) {
        console.error("Error updating table data:", error);
      }

      try {
        // Fetch all FB orders and filter in JS — closure-based .filter() breaks in SQLite/IPC mode
        const allFbOrders2 = await db.fbOrderData.toArray();
        const matchingOrder = allFbOrders2.find((order) => order.cart?.sOrderID === cart.sOrderID && order.table === cart.tableNo);

        if (matchingOrder) {
          const updatedItems = matchingOrder.cart.items.map((item) => {
            if (item.lineId === addedToCart[index].lineId) {
              return addedToCart[index]; // Update the modified item
            }
            return item;
          });
          // BUG-2103: this fbOrderData mirror summed the lines raw, so the persisted
          // Restaurant order carried a float-accumulated total/tax that disagreed with
          // the in-memory cart by a float epsilon after a reload.
          const total = roundTo(
            updatedItems.reduce((sum, item) => sum + item.nettotal, 0),
            precision
          );
          const totalQty = updatedItems.reduce((sum, item) => sum + item.weight, 0);
          const tax = roundTo(
            updatedItems.reduce((sum, item) => sum + item.taxAmount, 0),
            precision
          );
          const updatedOrder = {
            ...matchingOrder,
            cart: {
              ...matchingOrder.cart,
              items: updatedItems,
              total: total,
              tax: tax,
              totalQty: totalQty,
            },
          };

          updatedOrder.totalAmount = updatedItems.reduce((sum, item) => sum + item.nettotal, 0);

          await db.fbOrderData.put(updatedOrder);
        }
      } catch (error) {
        console.error("Error updating fbOrderData:", error);
      }
    }
    }
  } else {
    // Create a shallow copy of addToCart to work with the updated item
    let newAddToCart = { ...addToCart };
    newAddToCart.saleType = useRestaurantStore.getState().dineIn === "Y" ? "DI" : "TA";
    const cartObj = useCartStore.getState().cart;
    if (receiptlessReturn2Enabled && activeCartFromStorage?.isReturn === true && activeCartFromStorage?.isBlindReceipt === true) {
      newAddToCart.weight = -Math.abs(weight);
    } else {
      newAddToCart.weight = parseFloat(truncateToPrecision(weight, newAddToCart.isQtyDesimal));
    }

    const addOnsPriceSum = sumBy(newAddToCart?.selectedAddons, "price") || 0;
    delete newAddToCart.nextRule;

    const mrp = modifiedPrice && newAddToCart.taxFlag === "N" ? modifiedPrice : (parseFloat(newAddToCart.salePrice) + addOnsPriceSum) * parseFloat(weight);

    const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + newAddToCart.taxRate / 100) : mrp * (newAddToCart.taxRate / 100);
    let netStd = parseFloat(newAddToCart.sunitprice - newAddToCart.sunitprice / (1 + newAddToCart.taxRate / 100));
    let netStdValue =
      taxIncludeFlag === "Y"
        ? newAddToCart.sunitprice > 0
          ? parseFloat(parseFloat(newAddToCart.sunitprice - netStd).toFixed(precision))
          : 0
        : roundTo(newAddToCart.sunitprice ?? 0, precision);
    newAddToCart.taxAmount = taxIncludeFlag === "Y" ? tax : roundTo(tax, precision);
    // TIC-5459: a carried-over discount larger than this line's own mrp produced a negative
    // nettotal while taxAmount (computed from the full mrp above) stayed positive. Cap the
    // discount at mrp so a line can never be discounted below zero. Scoped to mrp >= 0 so
    // return/exchange lines (negative mrp) keep their existing, untouched behavior.
    const rawDiscount = newAddToCart.discount ? parseFloat(newAddToCart.discount) : 0;
    newAddToCart.discount = mrp >= 0 && rawDiscount > mrp ? mrp : rawDiscount;
    newAddToCart.nettotal = taxIncludeFlag === "Y" ? parseFloat(mrp - newAddToCart.discount) : roundTo(mrp - newAddToCart.discount + tax, precision);

    newAddToCart.nettotal = taxIncludeFlag === "Y" ? parseFloat(parseFloat(newAddToCart.nettotal).toFixed(precision)) : roundTo(newAddToCart.nettotal, precision);

    // BUG-2041 (Stage-1 gap, TIC-6916/TSK-3355): every sibling branch that mutates
    // weight/nettotal already guards against NaN reaching the cart -- qty-edit merge
    // (~L521: `!(x > 0)` catches an unparseable qty), cancel-restore, and
    // parked-restore. This new-line-add branch was the one path BUG-2041's fix pass
    // never touched. A NaN nettotal/taxAmount here (unparseable qty, or a product
    // record missing salePrice/taxRate) previously entered the cart silently,
    // contaminated every downstream total/tax sum (this file's own totals loop, every
    // PricingRules file, and finally useOrderProcessing.js's outbound payload), and
    // shipped to the backend as a literal JSON null against a NOT NULL column --
    // stranding the order in pending sync with no diagnosable cause.
    if (!Number.isFinite(newAddToCart.nettotal) || !Number.isFinite(newAddToCart.taxAmount)) {
      console.error(
        "[DATA-INTEGRITY] addProductToCart: rejected new line with non-finite total/tax",
        JSON.stringify({
          productId: newAddToCart.productId,
          name: newAddToCart.name,
          qty,
          weight: newAddToCart.weight,
          salePrice: newAddToCart.salePrice,
          taxRate: newAddToCart.taxRate,
          nettotal: newAddToCart.nettotal,
          taxAmount: newAddToCart.taxAmount,
        })
      );
      message.error(`${newAddToCart.name || "Item"} could not be added — invalid price or quantity.`);
      return;
    }

    newAddToCart.unitTax = newAddToCart?.isReturn
      ? newAddToCart.unitTax
      : taxIncludeFlag === "Y"
        ? parseFloat(parseFloat(newAddToCart.salePrice - newAddToCart.salePrice / (1 + newAddToCart.taxRate / 100)).toFixed(precision))
        : roundTo((newAddToCart.salePrice * newAddToCart.taxRate) / 100, precision);

    newAddToCart.netStd = isWalletReturn ? -Math.abs(netStdValue) : netStdValue;

    newAddToCart.lineId = uuidv4().replace(/-/g, "").toUpperCase();
    newAddToCart.salesRepId = salesRepresentDefaultLine.salesRepresentId;
    newAddToCart.salesRepName = Object.keys(salesRepresent).length > 0 ? salesRepresentDefaultLine.name : "";
    if (isWalletReturn) {
      newAddToCart.sunitprice = -Math.abs(newAddToCart.sunitprice);
      newAddToCart.listPrice = -Math.abs(newAddToCart.listPrice);
      newAddToCart.netStd = -Math.abs(newAddToCart.netStd);
      newAddToCart.unitTax = -Math.abs(newAddToCart.unitTax);
    }
    // Add the newly created item to the cart
    isNewLineAdded = true;
    addedToCart.unshift(newAddToCart);

    let latestIndex = 0; // Since we used unshift, the latest item is at index 0
    setSelectedRowKeys([latestIndex]);
    setSelectedProductInCart(newAddToCart);
    addToCart = { ...newAddToCart };
    deferredMsg = {
      type: qty < 0 ? "warning" : "success",
      text: `${newAddToCart.name} ${qty < 0 ? messages.errors.cart.removed_successfully : messages.errors.cart.added_successfully}`,
    };
    deferredPoleDisplay = newAddToCart;
  }
  // addedToCart = clubProductsWithAddOns(addedToCart, precision);
  let stockCheckResult = true;
  let isFinalValidation = false;
  let isCompleteSale = false;
  const isPreOrderPage = window.location.pathname.includes("/preorder");
  const hasChildItems = addedToCart?.some((item) => (Array.isArray(item.childItems) && item.childItems.length > 0) || (Array.isArray(item.children) && item.children.length > 0));

  if (hasChildItems && (totalQtyFlag || quantityChange)) {
    isCompleteSale = true;
  }
  // BUG-1640: block adding a product that has no resolvable price (removed from the
  // active price list in backoffice, comes back as isactive:true with priceList:null
  // and sunitprice/salePrice 0). posPriceEdit==="Y" items (e.g. BATTERY) are exempt —
  // those are meant to be manually priced by the cashier and legitimately have no
  // price-list entry. Scoped to the brand-new-line-add path only, same gate the
  // stock() check below already uses, so it never touches qty-increment/return/service/
  // layaway/offline flows.
  if (
    isNewLineAdded &&
    (addToCart.type === "I" || !addToCart.type) &&
    addToCart.layAway !== "Y" &&
    navigator.onLine &&
    !returnEnabled &&
    addToCart.posPriceEdit !== "Y" &&
    (!Array.isArray(addToCart.priceList) || addToCart.priceList.length === 0) &&
    !(Number(addToCart.sunitprice) > 0) &&
    !(Number(addToCart.salePrice) > 0) &&
    !(Number(addToCart.realPrice) > 0)
  ) {
    activeCartFromStorage.items = itemsSnapshot;
    setSelectedProductInCart({});
    if (isExchange) {
      useCartStore.getState().setExchangeCart({ ...activeCartFromStorage, items: itemsSnapshot });
    } else {
      setCart({ ...cart, items: itemsSnapshot });
    }
    Modal.info({
      icon: null,
      okText: "Ok",
      okButtonProps: { style: { display: "inline-block" } },
      content: (
        <div
          style={{
            fontSize: "18px",
            fontWeight: "500",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: 0,
            fontSize: "1vw",
          }}
        >
          <img src={NotFoundImg} alt="Not Found" style={{ width: "120px", height: "100px" }} />
          <p style={{ margin: "5px 0", fontSize: "1.5em", fontWeight: "600", color: "#0F0718" }}>{addToCart.value}</p>
          <p style={{ margin: "5px 0", fontSize: "2em", fontWeight: "600", color: "#0F0718" }}>Product Not Found</p>
          <hr style={{ width: "70%", color: "rgba(146,144,152,0.5" }} />
          <p style={{ color: "#0F0718", fontWeight: 400, fontSize: "1.25em", padding: "15px 15px 0 15px" }}>
            This product is not available for sale. Please check the price list configuration.
          </p>
        </div>
      ),
      style: {
        textAlign: "center",
        height: "50vh",
        width: "50%",
      },
      closable: false,
    });
    return;
  }
  if (
    (addToCart.type === "I" || !addToCart.type) &&
    addToCart.layAway !== "Y" &&
    // addToCart.productSegment !== "CB" &&
    navigator.onLine &&
    // cart.layAway !== "Y" &&
    tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" &&
    !deleteProductInCart &&
    !isPreOrderPage &&
    !returnEnabled
  ) {
    stockCheckResult = await stock({
      cart: { ...cart, items: addedToCart },
      setCart,
      tillData,
      setProductStock,
      isFinalValidation,
      setCart,
      isCompleteSale,
      setShowCompleteSaleStock,
      setCompleteSaleStockData,
      customMixHandler: false,
      setProductBOMStock,
      setProductBOMStockData,
    });
    if (!stockCheckResult) {
      activeCartFromStorage.items = itemsSnapshot;
      setSelectedProductInCart({});
      if (isExchange) {
        useCartStore.getState().setExchangeCart({ ...activeCartFromStorage, items: itemsSnapshot });
      } else {
        setCart({ ...cart, items: itemsSnapshot });
      }
      return;
    }
  }

  if (deferredMsg) {
    message[deferredMsg.type](deferredMsg.text, 0.1);
  }
  if (deferredPoleDisplay) {
    if (CWHardwareController) {
      CWPoleDisplay(deferredPoleDisplay, "Add item to cart");
    } else {
      PoleDisplay(deferredPoleDisplay, "Add item to cart");
    }
  }

  let totalTax = 0;
  let totalPrice = 0;
  let totalItemsQty = 0;
  let totalDiscounts = 0;
  for (let i = 0; i < addedToCart.length; i += 1) {
    // weight === 0 means the line's qty was fully removed — the cart UI hides these
    // lines (index.js cartItems filter) but keeps them in cart.items, so they must be
    // excluded here too or Subtotal/Tax/Total keep counting a line the user no longer sees.
    if (addedToCart[i]?.isCanceled === "Y" || addedToCart[i]?.weight === 0) {
      continue;
    }
    totalPrice += parseFloat(parseFloat(addedToCart[i].nettotal).toFixed(precision));
    totalItemsQty += addedToCart[i].weight;
    totalTax += parseFloat(parseFloat(addedToCart[i].taxAmount).toFixed(precision));
    totalDiscounts += parseFloat(parseFloat(addedToCart[i].discount).toFixed(precision));
    addedToCart[i].key = i;
  }

  const { roundedTotal: roundOffValue, roundOffAmount: totalRoundOff } = applyCurrencyRounding(totalPrice, getRoundingMethod(tillData), getPrecision(tillData));
  const isExchangeFlag = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  // BUG-2151: `cart` is the stale pre-call parameter (see activeCartFromStorage/addedToCart at
  // the top of this function) -- it still reflects the cart as it was BEFORE this delete, so
  // `cart.items.length === 0` was never true on the exact call that empties the cart (it was 1
  // going in). The old `deleteCart()` call gated on that condition was already a no-op even in
  // the rare case it did fire, since execution fell through to the setCart(cartObj) below
  // regardless, clobbering deleteCart's own reset -- so calling deleteCart()/starting a brand
  // new order (new sOrderID/documentno/docType) here was never actually load-bearing for
  // anyone. Simply clear the discount bookkeeping that would otherwise leak onto the next
  // product added to this same (now-empty) cart -- no need to reset order identity to do that.
  const hasActiveItems = addedToCart.some((item) => item?.isCanceled !== "Y" && item?.weight !== 0);
  if (!hasActiveItems && !isExchangeFlag) {
    activeCartFromStorage.couponInput = [];
    activeCartFromStorage.manualDiscountData = { lineLevelDiscount: [], totalLevelDiscount: [] };
    activeCartFromStorage.totalBillDicount = null;
  }
  let cartObj = {
    // ...cart,
    ...activeCartFromStorage,
    items: [...addedToCart],
    total: roundOffValue,
    tax: roundTo(totalTax, precision),
    discount: totalDiscounts,
    totalQty: totalItemsQty,
    roundOff: totalRoundOff,
  };

  if (cartObj.items.length === 0 && tillData.tillAccess.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && cartObj.description !== "") {
    let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
    orderTimeDetails = {
      ...orderTimeDetails,
      orderStartTime: "", // Update orderStartTime to current time
    };
    usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
  }

  let updatedCart = cartObj;

  if (cartObj.items?.length > 0) {
    let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
    orderTimeDetails = {
      ...orderTimeDetails,
      paymentStartTime: "",
      orderStartTime: orderTimeDetails.orderStartTime !== "" ? orderTimeDetails.orderStartTime : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"), // Update orderStartTime to current time
    };
    usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
    updatedCart = addedToCart.isReturn ? cartObj : await pricingRuleController(addToCart, cartObj, cart, setCart, cartRef, orderType);
  }

  if (updatedCart?.couponInput?.length > 0 && cart?.statusType !== "layAway") {
    // Use sequential for...of instead of Promise.all map — parallel execution causes
    // updatedCart reassignments to race and overwrite each other (stale closure).
    for (const coupon of updatedCart.couponInput) {
      updatedCart = await pricingRuleController(
        addToCart,
        updatedCart,
        cart,
        setCart,
        cartRef,
        orderType,
        true,
        coupon.couponCode,
        coupon.referenceId,
        coupon.mPricingCouponId,
        coupon.mPricingruleId
      );
    }
  }

  const lineLevelDiscounts = updatedCart?.manualDiscountData?.lineLevelDiscount || [];
  const totalLevelDiscountsRaw = updatedCart?.manualDiscountData?.totalLevelDiscount || [];

  // Total-level (TD) entries represent one cart-wide discount instance per rule, so dedup by
  // pricingRule is correct for them (unchanged from before).
  const uniqueTotalLevelDiscounts = totalLevelDiscountsRaw.reduce((acc, item) => {
    if (!acc.some((discount) => discount.pricingRule === item.pricingRule)) {
      acc.push(item);
    }
    return acc;
  }, []);

  // BUG-1755: line-level (FD/PD) entries must NOT be deduped by pricingRule id — two different
  // lines can carry the same discount type (same pricingRule) with different manually-entered
  // amounts. The previous dedup collapsed them into a single entry, and the replay step then
  // matched "any cart item that has this rule id in allDiscounts" rather than the owning line,
  // so that one entry's discountValue got broadcast onto every line that ever used the rule —
  // silently overwriting other lines' own discount amounts each time a new product was added
  // (which re-enters this function and re-runs this replay). Fix: keep every line-level entry
  // and match strictly by lineId; still replay in original apply order (sequenceNo) alongside
  // the total-level entries so FD/PD-vs-TD interaction ordering is unchanged.
  const discountsToReplay = [...lineLevelDiscounts, ...uniqueTotalLevelDiscounts].sort((a, b) => a.sequenceNo - b.sequenceNo);

  for (const item of discountsToReplay) {
    const pricingRule = manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule);
    if (!pricingRule) continue;

    if (pricingRule.type === "FD" || pricingRule.type === "PD") {
      const cartItem = updatedCart.items.find((ci) => ci.lineId === item.lineId);
      if (!cartItem) continue;
      const itemAllDiscounts = cartItem?.allDiscounts || [];
      const matchedRuleIndex = itemAllDiscounts.findIndex((d) => d.mPricingruleId === pricingRule.mPricingrulesId);
      if (matchedRuleIndex === -1) continue;
      const applyDiscount = pricingRule.type === "FD" ? CheckoutFlatDiscount : CheckoutPercentageDiscount;
      cartItem.priority = 0;
      // TIC-10890: pass the stored approver so the replay keeps the line's approval.
      updatedCart = await applyDiscount(cartItem, pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue, item.approver);
    } else if (pricingRule.type === "TD") {
      updatedCart = await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue, item.approver);
    }
  }

  // Use updatedCart (post-pricingRuleController) not stale cart/cartObj.
  // Also carry over couponInput discount type override so amount coupons re-apply correctly.
  // BUG-2151: only fall back to the stale `cart` param's totalBillDicount when the cart still
  // has active items -- otherwise this reapplies the bill discount right back onto the cart
  // this same call just emptied, undoing the clear above.
  const activeTotalBillDicount = updatedCart?.totalBillDicount || (hasActiveItems && cart.totalBillDicount);
  if (activeTotalBillDicount) {
    const matchingPricingRules = findPricingRuleById(activeTotalBillDicount);
    if (matchingPricingRules.length > 0) {
      const ruleToApply = { ...matchingPricingRules[0] };
      // Re-apply the discountType override from couponInput so amount coupons use "V" not their DB value.
      const matchedCoupon = (updatedCart?.couponInput || cart?.couponInput || []).find((c) => c.mPricingruleId === ruleToApply.mPricingrulesId);
      if (matchedCoupon?.discountType) {
        ruleToApply.discountType = matchedCoupon.discountType;
        ruleToApply.amountDiscount = matchedCoupon.amountDiscount ?? ruleToApply.amountDiscount;
        ruleToApply.percentageDiscount = matchedCoupon.percentageDiscount ?? ruleToApply.percentageDiscount;
      }
      await processBillDiscounts(ruleToApply, updatedCart, true);
      updatedCart = useCartStore.getState().cart;
    }
  }

  if (cart.manualDiscountApplied && cart.manualDiscountApplied !== 0 && cart.discountType === "TD") {
    setLoader(true);
    setTimeout(async () => {
      await processTotalManualDiscount(cart.manualDiscountApplied);
      setLoader(false);
    }, 200);
  }

  const layout = userAccessController.layout;
  if (layout === "2") {
    const cartData = cartObj ? { ...cartObj } : cart;
    if (cart?.items?.length > 0) {
      const pricingRules = getPricingRules();
      pricingRules.map(async (pr) => {
        const pricingRuleCount = pr.length;
        if (pricingRuleCount > 0) {
          pr.sort((a, b) => {
            if (a.priority === null && b.priority === null) {
              return 0;
            } else if (a.priority === null) {
              return 1;
            } else if (b.priority === null) {
              return -1;
            }
            return parseInt(a.priority, 10) - parseInt(b.priority, 10);
          });
          for (let i = 0; i < pricingRuleCount; i++) {
            const pricingRule = pr[i];
            if (dateValidator(pricingRule.startDate, pricingRule.endDate) && pricingRule.iscoupon === "N" && (pricingRule.type === "TD" || pricingRule.type === "TDF")) {
              // Skip non-coupon TD rules if the cart already has an active coupon applied.
              // Running them would overwrite the coupon discount on cart items.
              const cartHasCoupon = cartData.iscoupon === true || cartData.couponInput?.length > 0 || cartData.items?.some((item) => item.iscoupon === true);
              if (cartHasCoupon) continue;
              if (pricingRule.timeSpecific === "Y") {
                const weekDay = currentDay();
                const pStartTime = pricingRule.starttime.substring(11);
                const pEndTIme = pricingRule.endtime !== null ? pricingRule.endtime.substring(11) : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss").substring(11);
                const starttime = timeStamp().substring(0, 10) + " " + pStartTime;
                const endtime = timeStamp().substring(0, 10) + " " + pEndTIme;
                if (timeValidator(starttime, endtime) && pricingRule[weekDay] === "Y") {
                  updatedCart = await processBillDiscounts(pricingRule, cartData, false);
                }
              } else {
                updatedCart = await processBillDiscounts(pricingRule, cartData, false);
              }
            }
          }
        }
      });
    }
  }

  const itemSummery = {
    decimalItems: { totalQty: 0, totalAmount: 0 },
    nonDecimalItems: { totalQty: 0, totalAmount: 0 },
    itemsQty: 0,
    items: 0,
  };

  // Group items by tax rate
  // const groupedItems = (updatedCart.items || []).reduce((acc, item) => {
  //   const key = item.tax?.rate ?? item.taxRate ?? "0";
  //   acc[key] = acc[key] || [];
  //   acc[key].push(item);
  //   return acc;
  // }, {});

  const isInterState = getIsInterState(updatedCart);
  // Zero-weight lines (qty fully removed) are hidden from the invoice table
  // (see index.js cartItems filter) — exclude them from the tax breakup too.
  const groupedItemsByTaxRate = groupItemsByTaxRate(
    updatedCart.items.filter((item) => item.weight !== 0),
    precision,
    { isInterState }
  );

  let updatedTotalTax = 0;
  let updatedTotalPrice = 0;
  let updatedTotalItemsQty = 0;
  let updatedTotalDiscounts = 0;
  updatedCart.items = updatedCart.items.map((item, i) => {
    // weight === 0 means the line's qty was fully removed — keep it out of
    // Subtotal/Tax/Total the same way isCanceled lines are excluded.
    if (item?.isCanceled === "Y" || item?.weight === 0) {
      return item;
    }
    const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
    const nettotalFixed = item.nettotal;
    const taxAmountFixed = parseFloat(parseFloat(item.taxAmount).toFixed(precision));
    // const discountFixed = item.discount ? parseFloat(truncateToPrecision(item.discount, precision)) : 0;
    const discountFixed = item.discount ? parseFloat(parseFloat(item.discount).toFixed(precision)) : 0;

    // Update totals
    updatedTotalPrice += nettotalFixed;
    updatedTotalItemsQty += parseFloat(parseFloat(item.weight).toFixed(precision));
    updatedTotalTax += taxAmountFixed;
    updatedTotalDiscounts += discountFixed;

    item.discount = discountFixed;
    item.key = i;

    if (!item.isGiftCard) {
      const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;
      const lineTax = roundTo(item.listPrice * (1 + item.taxRate / 100), precision);
      const stdTax = roundTo(item.sunitprice * (1 + item.taxRate / 100), precision);
      // const grossUnit = Math.abs(item.sunitprice) - Math.abs(item.discount) / Math.abs(item.weight);
      const netList =
        taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100)).toFixed(precision)) : item.listPrice;
      const gross_list =
        taxIncludeFlag === "Y"
          ? item.listPrice
          : // parseFloat(parseFloat(lineTax).toFixed(precision));
            roundTo(lineTax, precision);
      // const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax).toFixed(precision));
      const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : roundTo(stdTax, precision);
      // const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : item.sunitprice + taxAmountFixed;
      const isWalletReturn = receiptlessReturn2Enabled && cart?.isReturn === true && cart?.isBlindReceipt === true;
      Object.assign(item, {
        linetax: nettotalFixed ? taxAmountFixed : 0,
        linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount).toFixed(precision)) : 0,
        linegross: nettotalFixed ? (isWalletReturn ? Math.abs(nettotalFixed) : nettotalFixed) : 0,
        linestd: nettotalFixed ? parseFloat(parseFloat(isWalletReturn ? Math.abs(grossStd * item.weight) : grossStd * item.weight).toFixed(precision)) : 0,
        netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) : 0,
        listprice: nettotalFixed ? (isWalletReturn ? -Math.abs(item.listPrice) : item.listPrice) : 0,
        grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) * (isWalletReturn ? 1 : item.isReturn ? -1 : 1) : 0,
        grossstd: nettotalFixed ? parseFloat(parseFloat(grossStd).toFixed(precision)) : 0,
        grosslist: nettotalFixed ? gross_list : 0,
        netList: nettotalFixed ? (isWalletReturn ? Math.abs(netList) : netList) : 0,
        unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) : 0,
        taxAmount: nettotalFixed ? taxAmountFixed : 0,
      });
    }
    // Update summary
    // const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
    // const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
    // itemSummery.itemsQty += item.weight;
    // itemSummery.items += 1;
    // itemSummery[summaryKey].totalQty += item.weight;
    // itemSummery[summaryKey].totalAmount += parseFloat(truncateToPrecision(item.nettotal, precision));

    if (Array.isArray(item.children) && item.children.length > 0) {
      item.children.forEach((child) => {
        const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

        itemSummery.itemsQty += child.weight;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += child.weight;
        itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal).toFixed(precision));
      });
    } else {
      const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
      const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

      itemSummery.itemsQty += item.weight;
      itemSummery.items += 1;
      itemSummery[summaryKey].totalQty += item.weight;
      itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal).toFixed(precision));
    }

    return item;
  });
  const calculateRoundingDifference = (value) => {
    const { roundOffAmount } = applyCurrencyRounding(value, getRoundingMethod(tillData), getPrecision(tillData));
    return parseFloat(parseFloat(roundOffAmount).toFixed(precision));
  };
  index = updatedCart?.items.findIndex((p) => p.productId === addToCart.productId && (p.upc || "") === (addToCart.upc || ""));
  // if (index !== -1 && addToCart.addNewLine !== "Y") {
  //   // Remove the item from its current position
  //   const removedItem = updatedCart?.items.splice(index, 1)[0];

  //   // Place the item at the beginning of the array
  //   updatedCart?.items.unshift(removedItem);
  // }

  if (index !== -1 && addToCart.addNewLine !== "Y") {
    const layout = userAccessController.layout;
    if (layout === "3" || addedToCart[index]?.children || addedToCart[index]?.children?.length > 0) {
      updatedCart.items[index].quantity += addToCart.quantity;
      // updatedCart.items[index].weight += addToCart.weight;
    } else {
      const removedItem = updatedCart?.items.splice(index, 1)[0];

      // Place the item at the beginning of the array
      updatedCart?.items.unshift(removedItem);
    }
  }
  const { roundedTotal: updatedRoundedTotal, roundOffAmount: updatedTotalRoundOffAmount } = applyCurrencyRounding(updatedTotalPrice, getRoundingMethod(tillData), getPrecision(tillData));
  const updatedTotalRoundOff = parseFloat(parseFloat(updatedTotalRoundOffAmount).toFixed(precision));
  // Sum Line Totals
  const sumLineTotals = (lines) => {
    let totalLineGross = 0;
    let totalLineTax = 0;

    lines.forEach((line) => {
      totalLineGross += line.nettotal;
      totalLineTax += line.taxAmount;
    });

    return { totalLineGross, totalLineTax };
  };
  // Compare and Adjust Order Totals

  const adjustOrderTotals = (order, lines) => {
    const { totalLineGross, totalLineTax } = sumLineTotals(lines);
    let adjustedOrderGross = order.total;
    let adjustedOrderTax = order.tax;

    const grossDifference = totalLineGross - adjustedOrderGross;
    const taxDifference = totalLineTax - adjustedOrderTax;

    // If grossDifference equals the existing roundOff, the gap is due to intentional
    // currency rounding — preserve the rounded total instead of correcting it back.
    // grossDifference = gross - rounded; roundOff = rounded - gross; sum = 0 when equal.
    const existingRoundOff = order.roundOff || 0;
    const isRoundingDifference = Math.abs(grossDifference + existingRoundOff) < 0.01;

    if (Math.abs(grossDifference) > 0.01 && !isRoundingDifference) {
      adjustedOrderGross += grossDifference;
    }

    if (Math.abs(taxDifference) > 0.01) {
      adjustedOrderTax += taxDifference;
    }

    return {
      ...order,
      total: adjustedOrderGross,
      tax: adjustedOrderTax,
    };
  };
  updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);
  // const updatedTotalStd = updatedCart.items.reduce((sum, item) => sum + item.linestd, 0);
  const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
  const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
  const items = [...updatedCart?.items];
  let totalTaxItems = 0;
  for (let i = 0; i < items.length; i += 1) {
    if (items[i]?.isCanceled === "Y" || items[i]?.weight === 0) continue;
    totalTaxItems += items[i].taxAmount;
  }
  let finalCartObj = {
    ...updatedCart,
    layAway: layAwayFlag,
    items: [...updatedCart?.items],
    discount: updatedTotalDiscounts,
    totalQty: parseFloat(updatedTotalItemsQty.toFixed(precision)),
    roundOff: updatedTotalRoundOff,
    total: updatedRoundedTotal,
    tax: roundTo(totalTaxItems, precision),
    payments: updatedCart?.items.length > 0 ? updatedCart.payments : [],
    paid: updatedCart?.items.length > 0 ? updatedCart.paid : 0,
    subTotal: parseFloat(parseFloat(updatedTotalPrice - totalTaxItems).toFixed(precision)),
    amountInWords: numberToWords(updatedRoundedTotal),
    itemsSummery: itemSummery,
    groupedItemsByTaxRate,
    saleType: usePOSConfigStore.getState().saleTypeData?.cwrSaletype || {},
    totalStd: updatedTotalStd,
  };
  // Sync selectedProductInCart with the post-pricing-rule item so that the
  // next '+'/'-' press sees the discountName/discountType that pricing rules
  // (e.g. BuyXPayYOfSame / BUY 1 GET 1) just stamped on the cart item.
  // Without this, findIndex checks !p.discountName and misses the item,
  // causing a spurious new cart line to be created on rapid '+' presses.
  const updatedSelectedItem = updatedCart?.items?.find((item) => item.lineId === addToCart.lineId);
  // BUG-2547: after a Custom Mix child's qty edit, keep that child selected. Selecting the mix
  // row made the next typed qty rescale the whole mix.
  const editedMixChild = editedMixChildLineId ? updatedSelectedItem?.children?.find((child) => child.lineId === editedMixChildLineId) : undefined;
  if (editedMixChild && editedMixChild.weight > 0) {
    setSelectedProductInCart(editedMixChild);
    if (editedMixChild.key != null) setSelectedRowKeys([editedMixChild.key]);
  } else {
    setSelectedProductInCart(updatedSelectedItem || addToCart);
    const addToCartIdx = addedToCart.findIndex((item) => item.productId === addToCart.productId);
    if (addToCartIdx >= 0) setSelectedRowKeys([addToCartIdx]);
  }
  if (returnThisReceipt === "true" && selectedReturnType === "STORE_CREDIT") {
    localStorage.setItem("selectedReturnType", "STORE_CREDIT");
    useTransactionStore.getState().setSelectedReturnType("STORE_CREDIT");
    const walletPayment = tillData.tillAccess.csBunit.paymentMethodList.find((pm) => pm.isWallet === "Y");
    if (walletPayment) {
      setSelectedPaymentMethod(walletPayment);
    }
  }
  if (useTransactionStore.getState().selectedReturnType === "EXCHANGE") {
    trace("SMAddProduct", "addProductToCart commit", {
      productId: addToCart?.productId,
      isExchange,
      returnThisReceipt,
      committedTo: isExchange && !returnThisReceipt ? "exchangeCart" : "cart",
      committedWeight: updatedSelectedItem?.weight,
      committedIsReturn: updatedSelectedItem?.isReturn,
    });
  }
  if (isExchange && !returnThisReceipt) {
    const returnCartObj = useCartStore.getState().cart;
    const finalExchangeCart = { ...finalCartObj, returnsOrderId: returnCartObj?.sOrderID || null };
    useCartStore.getState().setExchangeCart(finalExchangeCart);
  } else {
    setCart(finalCartObj);
  }
  if (posConfig?.saveParkedBills === "Y" && finalCartObj?.isRetrived === "Y") {
    setManualDiscountInput(null);
    setSelectedManualDiscountTypeValue(null);
  }
  useTransactionStore.getState().setReturnThisReceipt(null);
};
